import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, InputGroup, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import i18n from "../../../i18n";
import { fetchActionsPermissions, fetchActionsPermissionsLabels } from "../../../redux/slices/actionsPermissionsSlice";
import "./ManagePermissions.css";
import { fetchRoles } from "../../../redux/slices/rolesSlice";
import { fetchUsers } from "../../../redux/slices/userSlice";
import { normalizeActionLabels, pickActionBooleanMap, getAssignmentInfo } from "../../../components/permissions/helpers/PermissionsHelper/PermissionsHelper";    

const ITEMS_PER_PAGE = 10;

const matchesFilter = (assignmentType, filterType) => {
    if (filterType === "all") return assignmentType === "user" || assignmentType === "role" || assignmentType === "both";
    if (filterType === "both") return assignmentType === "both";
    return assignmentType === filterType;
};

const ManagePermissions = () => {

    console.log("ManagePermissions rendered");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { items, labels, isLoading, error } = useSelector((state) => state.actions);
    const { items: users = [] } = useSelector((state) => state.users);
    const { items: roles = [] } = useSelector((state) => state.roles);

    const [filterType, setFilterType] = useState("all");
    const [sortBy, setSortBy] = useState("alpha");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    useEffect(() => {
        dispatch(fetchActionsPermissions());
        dispatch(fetchActionsPermissionsLabels());
    }, [dispatch]);

    useEffect(() => {
      dispatch(fetchUsers());
      dispatch(fetchRoles());
    }, [dispatch]);

    const normalizedLabels = useMemo(
        () => normalizeActionLabels(labels), 
        [labels]
    );

    const labelKeys = useMemo(
        () => normalizedLabels.map((label) => label.key), 
        [normalizedLabels]
    );

    const enrichedItems = useMemo(() => {
        return (Array.isArray(items) ? items : []).map((item) => {
            const assignment = getAssignmentInfo(item);
            const actionMap = pickActionBooleanMap(item, labelKeys);
            const allowedCount = Object.entries(actionMap).filter(([action, allowed]) => {
                if (typeof allowed !== "boolean") return false;
                if (!action.startsWith("action_")) return false;
                return allowed;
            }).length;

            return {
                raw: item,
                id: item.id,
                assignmentType: assignment.type,
                assignmentLabel: assignment.label,
                allowedCount,
            };
        });
    }, [items, labelKeys]);

    const filteredItems = useMemo(() => {
        const query = search.trim().toLowerCase();

        const filtered = enrichedItems.filter((item) => {
            if (!matchesFilter(item.assignmentType, filterType)) return false;
            if (!query) return true;
            return item.assignmentLabel.toLowerCase().includes(query);
        });

        return filtered.sort((a, b) => {
            if (sortBy === "actions_desc") return b.allowedCount - a.allowedCount;
            if (sortBy === "actions_asc") return a.allowedCount - b.allowedCount;
            return a.assignmentLabel.localeCompare(b.assignmentLabel);
        });
    }, [enrichedItems, filterType, search, sortBy]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    const safePage = Math.min(page, totalPages);

    useEffect(() => {
        setPage(1);
    }, [filterType, sortBy, search]);

    const pagedItems = useMemo(() => {
        const start = (safePage - 1) * ITEMS_PER_PAGE;
        return filteredItems.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredItems, safePage]);

    return (
        <Container className="py-4 permissions-shell fade-in-up">
            <div className="permissions-toolbar mb-4 mt-5">
                <div>
                    <h2 className="permissions-title mb-1">{i18n.t("pages.ManagePermissions.title")}</h2>
                    <p className="permissions-subtitle mb-0">{i18n.t("pages.ManagePermissions.subtitle")}</p>
                </div>
                <Button className="permissions-cta" onClick={
                    () => navigate("/manage-permissions/new", { 
                        state: {
                            users: users,
                            roles: roles,
                        }
                    })
                }> {i18n.t("pages.ManagePermissions.create_actions_permissions")} </Button>
            </div>

            <Card className="permissions-panel mb-3">
                <Card.Body>
                    <Row className="g-2 align-items-end">
                        <Col xs={12} md={4}>
                            <Form.Label>{i18n.t("pages.ManagePermissions.filter_type")}</Form.Label>
                            <Form.Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                <option value="all">{i18n.t("pages.ManagePermissions.filter_all")}</option>
                                <option value="user">{i18n.t("pages.ManagePermissions.filter_user")}</option>
                                <option value="role">{i18n.t("pages.ManagePermissions.filter_role")}</option>
                                <option value="both">{i18n.t("pages.ManagePermissions.filter_user_role")}</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label>{i18n.t("pages.ManagePermissions.sort_by")}</Form.Label>
                            <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                <option value="alpha">{i18n.t("pages.ManagePermissions.sort_alpha")}</option>
                                <option value="actions_desc">{i18n.t("pages.ManagePermissions.sort_actions_desc")}</option>
                                <option value="actions_asc">{i18n.t("pages.ManagePermissions.sort_actions_asc")}</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label>{i18n.t("pages.ManagePermissions.search_label")}</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    value={search}
                                    placeholder={i18n.t("pages.ManagePermissions.search_placeholder")}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger">{typeof error === "string" ? error : i18n.t("pages.ManagePermissions.generic_error")}</Alert>}

            <Card className="permissions-panel">
                <Card.Body>
                    {isLoading ? (
                        <div className="d-flex align-items-center gap-2 py-3">
                            <Spinner size="sm" />
                            <span>{i18n.t("pages.ManagePermissions.loading")}</span>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <p className="permissions-empty-state mb-0">{i18n.t("pages.ManagePermissions.empty")}</p>
                    ) : (
                        <>
                            <Table responsive hover className="mb-3">
                                <thead>
                                    <tr>
                                        <th>{i18n.t("pages.ManagePermissions.table_assignment")}</th>
                                        <th>{i18n.t("pages.ManagePermissions.table_type")}</th>
                                        <th>{i18n.t("pages.ManagePermissions.table_allowed")}</th>
                                        <th className="text-end">{i18n.t("pages.ManagePermissions.table_actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedItems.map((item) => (
                                        <tr key={item.id}>
                                            <td className="fw-semibold">{item.assignmentLabel}</td>
                                            <td>
                                                <span 
                                                    className="fw-bold shadow-sm"
                                                    style={{
                                                        backgroundColor: item.assignmentType === "role" 
                                                            ? "var(--success-green)" 
                                                            : item.assignmentType === "user"
                                                                ? "var(--action-color)"
                                                                : "var(--brand-primary)", 
                                                        color: "var(--white)",
                                                        padding: "5px 10px",
                                                        borderRadius: "var(--btn-radius)",
                                                        fontSize: "0.75rem",
                                                        display: "inline-block",
                                                        letterSpacing: "0.5px"
                                                    }}
                                                >
                                                    {item.assignmentType === "user"
                                                        ? i18n.t("pages.ManagePermissions.filter_user")
                                                        : item.assignmentType === "role"
                                                            ? i18n.t("pages.ManagePermissions.filter_role")
                                                            : i18n.t("pages.ManagePermissions.filter_user_role")}
                                                </span>
                                            </td>
                                            <td className="fw-bold txt-subtle align-middle">{item.allowedCount}</td>
                                            <td className="text-end">
                                                <button 
                                                    className="btn btn-secondary" 
                                                    style={{ padding: "4px 12px", fontSize: "0.8rem" }}
                                                    onClick={() => navigate(`/manage-permissions/${item.id}`, { 
                                                        state: { users: users, roles: roles }
                                                    })}
                                                >
                                                    {i18n.t("pages.ManagePermissions.open")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <div className="permissions-pagination-wrap">
                                <small className="text-muted">
                                    {i18n.t("pages.ManagePermissions.pagination_summary", {
                                        from: filteredItems.length === 0 ? 0 : (safePage - 1) * ITEMS_PER_PAGE + 1,
                                        to: Math.min(safePage * ITEMS_PER_PAGE, filteredItems.length),
                                        total: filteredItems.length,
                                    })}
                                </small>
                                <Pagination className="mb-0">
                                    <Pagination.Prev disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                                    <Pagination.Item active>{safePage}</Pagination.Item>
                                    <Pagination.Next disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                                </Pagination>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ManagePermissions;
